import { cloneElement, useEffect, useState } from "react";
import { Head, Link, router } from "@inertiajs/react";
import { useConfirmDialog } from "@/components/confirm-dialog-provider";
import AuthLayout from "@/layouts/auth-layout";
import Drawer from "@/components/drawer";
import {
  NewOrderedRecordForm,
  OrderedRecordForm,
  StepFieldsForm,
  nextIndex
} from "@/pages/manage/products/manage-step-edit-drawer";
import {
  edit_manage_product_step_path,
  manage_product_path,
  manage_product_step_path,
  manage_products_path
} from "@/routes";

function templatesPath(product, step) {
  return `${manage_product_step_path(product.id, step.id)}/templates`;
}

const SUMMARY_LIST_LIMIT = 10;

function SummaryList({ items, emptyLabel }) {
  const visibleItems = items.slice(0, SUMMARY_LIST_LIMIT);
  const remainingCount = Math.max(items.length - visibleItems.length, 0);

  if (visibleItems.length === 0) {
    return <p className="workspace-card-description">{emptyLabel}</p>;
  }

  return (
    <ul className="workspace-card-summary-list">
      {visibleItems.map((item, index) => (
        <li key={`${item}-${index}`} className="workspace-card-summary-list__item">
          <span title={item}>{item}</span>
        </li>
      ))}
      {remainingCount > 0 ? (
        <li className="workspace-card-summary-list__more">… + {remainingCount} flere</li>
      ) : null}
    </ul>
  );
}

function SectionCard({ eyebrow, title, summary, summaryItems = null, emptySummary = null, meta, onEdit, href = null }) {
  const action = href ? (
    <Link href={href} className="btn btn-primary w-100">
      Redigér
    </Link>
  ) : (
    <button type="button" className="btn btn-primary w-100" onClick={onEdit}>
      Redigér
    </button>
  );

  return (
    <article className="card workspace-step-grid-card h-100">
      <div className="card-body d-flex flex-column">
        <div>
          <div className="workspace-card-eyebrow">{eyebrow}</div>
          <h3 className="workspace-card-title">{title}</h3>
        </div>

        {summaryItems ? (
          <SummaryList items={summaryItems} emptyLabel={emptySummary} />
        ) : (
          <p className="workspace-card-description">{summary}</p>
        )}

        <div className="workspace-step-grid-card__footer mt-auto">
          <div className="d-flex flex-wrap gap-3">
            {meta.map((item) => (
              <span key={item} className="workspace-chip">{item}</span>
            ))}
          </div>

          <div className="pt-2">
            {action}
          </div>
        </div>
      </div>
    </article>
  );
}

function StepInfoSummary({ step, onEdit }) {
  const showStepNumber = Number(step.index) > 0;

  return (
    <section className="card workspace-product-summary">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-4">
          <div>
            {showStepNumber ? (
              <div className="workspace-card-eyebrow">Trin {Number(step.index)}</div>
            ) : null}
            <h2 className={`workspace-product-summary__title${showStepNumber ? "" : " mt-0"}`}>{step.name}</h2>
          </div>

          <button type="button" className="btn btn-primary" onClick={onEdit}>
            Redigér
          </button>
        </div>

        <div className="workspace-product-summary__field">
          <div className="workspace-product-summary__field-label">Beskrivelse</div>
          <p className="workspace-product-summary__description">{step.description || "Ingen beskrivelse."}</p>
        </div>

        <div className="workspace-product-summary__field">
          <div className="workspace-product-summary__field-label">Kriterier for at trinnet er løst</div>
          <p className="workspace-product-summary__description">{step.completion_criteria || "Ingen kriterier angivet."}</p>
        </div>
      </div>
    </section>
  );
}

function OrderedDrawerContent({
  product,
  step,
  items,
  collection,
  paramKey,
  label,
  newLabel,
  emptyLabel,
  descriptionLabel = null,
  minIndex = 0
}) {
  return (
    <div className="workspace-content workspace-content--drawer">
      <div className="vstack gap-4 workspace-drawer-form-card card card-body">
        <div className="workspace-inline-list">
          {items.map((item) => (
            <OrderedRecordForm
              key={item.id}
              product={product}
              step={step}
              record={item}
              collection={collection}
              paramKey={paramKey}
              label={label}
              onDeleteLabel="Slet"
              descriptionLabel={descriptionLabel}
              minIndex={minIndex}
            />
          ))}
          <NewOrderedRecordForm
            product={product}
            step={step}
            collection={collection}
            paramKey={paramKey}
            label={newLabel}
            submitLabel="Opret"
            defaultIndex={nextIndex(items, minIndex)}
            descriptionLabel={descriptionLabel}
            minIndex={minIndex}
          />
        </div>

        {items.length === 0 ? (
          <div className="workspace-editor-panel__empty">{emptyLabel}</div>
        ) : null}
      </div>
    </div>
  );
}

function EditManageStep({ product, step, errors = [], show_basic_info_drawer: showBasicInfoDrawer = false, open_basic_info_signal: openBasicInfoSignal = 0 }) {
  const [activeDrawer, setActiveDrawer] = useState(showBasicInfoDrawer ? "basic" : null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const subSteps = step.sub_steps_attributes || [];
  const participants = step.step_participants_attributes || [];
  const stepTemplates = step.step_templates_attributes || [];
  const stepTemplateNames = stepTemplates
    .map((stepTemplate) => stepTemplate.content_kind === "file" ? stepTemplate.name : stepTemplate.template?.name)
    .filter(Boolean);
  const updatePath = manage_product_step_path(product.id, step.id);

  useEffect(() => {
    if (openBasicInfoSignal > 0) {
      setActiveDrawer("basic");
    }
  }, [openBasicInfoSignal]);

  return (
    <>
      <Head title={`Redigér ${step.name}`} />

      <section className="workspace-content">
        <div className="vstack gap-4">
          {errors.length > 0 ? (
            <div className="alert alert-danger mb-0">{errors.join(", ")}</div>
          ) : null}

          <StepInfoSummary step={step} onEdit={() => setActiveDrawer("basic")} />

          <div className="row g-4">
            <div className="col-md-6 col-xl-4">
              <SectionCard
                eyebrow="Undertrin"
                title="Undertrin"
                summary={subSteps.map((subStep) => subStep.name).join(", ") || "Ingen undertrin."}
                meta={[`${subSteps.length} i alt`]}
                onEdit={() => setActiveDrawer("substeps")}
              />
            </div>

            <div className="col-md-6 col-xl-4">
              <SectionCard
                eyebrow="Roller"
                title="Involverede"
                summary={participants.map((participant) => participant.name).join(", ") || "Ingen roller."}
                meta={[`${participants.length} i alt`]}
                onEdit={() => setActiveDrawer("participants")}
              />
            </div>

            <div className="col-md-6 col-xl-4">
              <SectionCard
                eyebrow="Dokumentmaterialer"
                title="Skabeloner og filer"
                summaryItems={stepTemplateNames}
                emptySummary="Ingen skabeloner eller filer."
                meta={[`${stepTemplates.length} i alt`]}
                href={templatesPath(product, step)}
              />
            </div>
          </div>
        </div>
      </section>

      <Drawer
        isOpen={activeDrawer === "basic"}
        onClose={() => setActiveDrawer(null)}
        title="Redigér trin"
        size="md"
        closeDisabled={isSubmitting}
      >
        <div className="workspace-content workspace-content--drawer">
          <div className="vstack gap-4 workspace-drawer-form-card card card-body">
            <StepFieldsForm
              step={step}
              updatePath={updatePath}
              onProcessingChange={setIsSubmitting}
            />
          </div>
        </div>
      </Drawer>

      <Drawer
        isOpen={activeDrawer === "substeps"}
        onClose={() => setActiveDrawer(null)}
        title="Redigér undertrin"
        size="md"
      >
        <OrderedDrawerContent
          product={product}
          step={step}
          items={subSteps}
          collection="sub_steps"
          paramKey="sub_step"
          label="Titel"
          newLabel="Titel"
          title="Undertrin"
          eyebrow="Undertrin"
          emptyLabel="Ingen undertrin endnu."
          descriptionLabel="Beskrivelse"
          minIndex={1}
        />
      </Drawer>

      <Drawer
        isOpen={activeDrawer === "participants"}
        onClose={() => setActiveDrawer(null)}
        title="Redigér involverede"
        size="md"
      >
        <OrderedDrawerContent
          product={product}
          step={step}
          items={participants}
          collection="step_participants"
          paramKey="step_participant"
          label="Rolle"
          newLabel="Ny rolle"
          title="Roller og ansvarlige"
          eyebrow="Roller"
          emptyLabel="Ingen roller endnu."
        />
      </Drawer>

    </>
  );
}

function EditManageStepLayout({ page }) {
  const { product, step } = page.props;
  const { confirm } = useConfirmDialog();
  const [openBasicInfoSignal, setOpenBasicInfoSignal] = useState(0);

  const destroyStep = async () => {
    const confirmed = await confirm({
      title: "Slet trin",
      description: `Vil du slette "${step.name}"?`,
      confirmLabel: "Slet",
      confirmVariant: "danger"
    });

    if (!confirmed) return;

    router.delete(manage_product_step_path(product.id, step.id));
  };

  return (
    <AuthLayout
      header={{
        breadcrumbs: [
          { label: "Administration", href: manage_products_path() },
          { label: "Alle produkter", href: manage_products_path() },
          { label: product.name, href: `${manage_product_path(product.id)}/edit` },
          { label: step.name }
        ],
        title: `Redigér ${step.name}`,
        actions: [
          {
            href: `${manage_product_path(product.id)}/edit`,
            label: "Tilbage",
            variant: "outline-secondary"
          },
          {
            label: "Redigér",
            variant: "primary",
            onClick: () => setOpenBasicInfoSignal((currentSignal) => currentSignal + 1)
          },
          {
            label: "Slet",
            variant: "danger",
            onClick: destroyStep
          }
        ]
      }}
    >
      {cloneElement(page, { open_basic_info_signal: openBasicInfoSignal })}
    </AuthLayout>
  );
}

EditManageStep.layout = (page) => <EditManageStepLayout page={page} />;

export default EditManageStep;
